import { ReactElement, UIEvent, MouseEvent, useState, SetStateAction } from "react";
import { IIndicatorEvent } from "../../../../../services/http/indicatorstatushistory/models/IIndicatorEvent";
import NoData from "../../../../../components/Shared/NoData";
import { useDependency } from "../../../../../contexts/DependencyContext";
import { DateService } from "../../../../../services/types/DateService";
import styles from "./IndicatorEventsTable.module.scss";
import { className, getColorVariantClassname } from "../../../../../shared/helpers";
import { useTimeMachineUpdate } from "../../../../../contexts/TimeMachineContext";
import { useTooltip } from "../../../../../hooks/UseTooltip";

import Pagination from "../Pagination";
import useWindowWidth from "../../../../../hooks/UseWindowWidth";

interface IIndicatorEventsTableProps
{
    events: IIndicatorEvent[]
}

interface IThItem
{
    label: string,
    isFixed: boolean
}

function IndicatorEventsTable({ events }: IIndicatorEventsTableProps): ReactElement
{
    const dateService = useDependency(DateService);
    const timeMachineUpdate = useTimeMachineUpdate();
    const { setupTooltip, clearTooltip, Tooltip } = useTooltip();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(6);
    const [totalLinks] = useState(8);
    const [totalLinksMobile] = useState(2);
    const isDesktop = useWindowWidth(992);

    const paginate = (pageNumber: SetStateAction<number>) => setCurrentPage(pageNumber);

    const previousPage = () => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1);
    const nextPage = () => setCurrentPage(currentPage === Math.ceil(events.length / rowsPerPage)  ? currentPage : currentPage + 1);

    function getLengthText(event: IIndicatorEvent): string
    {
        const { length } = event;
        const secondsInMinute = 60;
        const secondsInHour = secondsInMinute * 60;
        const secondsInDay = secondsInHour * 24;
        const secondsInYear = secondsInDay * 365;

        if (length < secondsInMinute)
            return `${Math.max(length, 0).toFixed(0)} seconds`;

        if (length < secondsInHour)
            return `${(length / secondsInMinute).toFixed(0)} minutes`;

        if (length < secondsInDay)
            return `${(length / secondsInHour).toFixed(0)} hours`;

        if (length < secondsInYear)
            return `${(length / secondsInDay).toFixed(0)} days`;

        return `${(length / secondsInYear).toFixed(0)} years`;
    }

    function handleScroll(e: UIEvent): void
    {
        const target = e.currentTarget as HTMLDivElement;

        // Toggle shadow class
        if (target.scrollLeft > 0) target.classList.add(styles.withShadow);
        else target.classList.remove(styles.withShadow);
    }

    function handleHover(e: MouseEvent): void
    {
        const target = e.target as HTMLElement;

        if (target && target.tagName === "TD")
        {
            const rowIndex = target.parentElement?.dataset.row;
            const rows = document.querySelectorAll("tr");
            const className = "hovering";

            for (const row of rows)
                row.dataset.row === rowIndex
                    ? row.classList.add(className)
                    : row.classList.remove(className);
        }
    }

    function setTimeMachine(event: IIndicatorEvent)
    {
        timeMachineUpdate(new Date(event.date));
    }

    function handleMouseEnter(event: MouseEvent): void
    {
        const { x, y } = event.currentTarget.getBoundingClientRect();

        setupTooltip({
            position: { x: x - 40, y: y - 10 },
            customClassName: styles.tooltip,
            content: "Go To Date"
        });
    }


    const thList = [
        { label: "", isFixed: true },
        { label: "", isFixed: true },
        { label: "Date", isFixed: true },
        { label: "Time", isFixed: false },
        { label: "Length", isFixed: false },
        { label: "Details", isFixed: false }
    ];

    const indexOfLastEvent = currentPage * rowsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - rowsPerPage;
    const currentEvent = events.slice(indexOfFirstEvent, indexOfLastEvent);

    return events.length > 0
        ? 
        <div>
        <div
            className={styles.tableWrapper}
            onScroll={handleScroll}
            onMouseMove={handleHover}
        >
            <table className={styles.fixedTable}>
                <thead>
                    <tr>
                        {thList.map((th: IThItem, i: number) => th.isFixed ? <th key={i}>{th.label}</th> : null)}
                    </tr>
                </thead>
                <tbody>
                    {currentEvent.map((x, i) => (
                        <tr key={i} data-row={i}>

                            <td>
                                <span {...className(styles.dot, getColorVariantClassname(x.indicatorStatusId))} />
                            </td>
                            <td>
                                <button className={styles.dateBtn} onClick={() => setTimeMachine(x)} onMouseEnter={handleMouseEnter} onMouseLeave={clearTooltip}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
                                        <g transform="translate(-1012.071 -958.5)">
                                            <g transform="translate(7864.254 -13938.535)">
                                                <path d="M-6847.3 14912v-.8c0-.5.4-.9.9-.9h.1c.5 0 1 .4 1 .9v.7c0 4.5 3.6 8.2 8.1 8.2s8.2-3.6 8.2-8.1-3.6-8.2-8.1-8.2c-1.8 0-3.6.6-5.1 1.8h.1c.3 0 .5.2.5.5s-.2.5-.5.5c-.7 0-1.5 0-2.2-.1-.2-.1-.3-.3-.3-.5 0 0 0 .1.1-2 0-.3.2-.5.5-.5s.5.2.5.5v.2c4.3-3.5 10.7-2.9 14.2 1.4s2.9 10.7-1.4 14.2-10.7 2.9-14.2-1.4c-1.6-1.8-2.4-4-2.4-6.4z" />
                                                <path d="m-6835 14916.7-3-3c-.2-.2-.2-.4-.2-.6v-5.7c0-.5.4-.8.8-.8.5 0 .8.4.8.8v5.3l2.8 2.8c.3.3.3.9 0 1.2-.4.3-.9.3-1.2 0z" />
                                            </g>
                                        </g>
                                    </svg>
                                </button>
                            </td>
                            <td>{dateService.format(new Date(x.date), 'MM/dd/yy')}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <table className={styles.dynamicTable}>
                <thead>
                    <tr>
                        {thList.map((th: IThItem, i: number) => !th.isFixed ? <th key={i}>{th.label}</th> : null)}
                    </tr>
                </thead>
                <tbody>
                    {currentEvent.map((x, i) => (
                        <tr key={i} data-row={i}>
                            <td>{dateService.format(new Date(x.date), 'HH:mm:ss')}</td>
                            <td className="highlighted">{getLengthText(x)}</td>
                            <td>{x.summary}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            
            <Tooltip />
        </div>
        {events.length > rowsPerPage &&
        <div >
            <Pagination rowsPerPage={rowsPerPage} totalRows={events.length} paginate={paginate} previousPage={previousPage} nextPage={nextPage} currentPage={currentPage} totalLinks={isDesktop ? totalLinks : totalLinksMobile}/>
        </div>
        }
        </div>
        : <NoData message="No events to display" />;
}

export default IndicatorEventsTable;
